import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Project } from '../types';
import { formatWorkOrderNumber } from '../types';
import type { BaseFormSectionProps } from '../types/project-form-types';
import { tenantsApi, type Tenant } from '../../api/tenants-api';

/**
 * Props específicas para ProjectBasicInfo
 */
interface ProjectBasicInfoProps extends BaseFormSectionProps {
  isEditMode: boolean;
  currentProject: Project | null;
  nextProjectNumber?: number | null;
}

/**
 * ProjectBasicInfo - Sección de información básica del proyecto
 *
 * @remarks
 * Incluye:
 * - Número de orden de trabajo (solo en edición)
 * - Nombre del proyecto
 * - Descripción
 * - Tenant/Cliente (solo en creación)
 * - Estado (solo en edición)
 *
 * **Cumple con:**
 * - P1: Componente < 200 líneas
 * - P3: Documentación JSDoc completa
 */
export function ProjectBasicInfo({
  formData,
  isLoading,
  isEditMode,
  currentProject,
  nextProjectNumber,
  onFormDataChange,
}: ProjectBasicInfoProps): JSX.Element {
  const { t } = useTranslation('projects');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);

  // Cargar tenants cuando el componente se monta (solo en modo creación)
  useEffect(() => {
    if (!isEditMode) {
      const loadTenants = async () => {
        try {
          setLoadingTenants(true);
          const fetchedTenants = await tenantsApi.getAll();
          setTenants(fetchedTenants);
          console.log('✅ Tenants cargados:', fetchedTenants);
        } catch (error) {
          console.error('❌ Error cargando tenants:', error);
        } finally {
          setLoadingTenants(false);
        }
      };
      void loadTenants();
    }
  }, [isEditMode]);

  // Mostrar número de orden en creación (próximo) o edición (asignado)
  const displayClientNumber = isEditMode
    ? currentProject?.clientNumber
    : (tenants.find(t => t.id === formData.tenantId) as any)?.clientNumber;
  const displayProjectNumber = isEditMode ? currentProject?.projectNumber : nextProjectNumber;

  // Determinar el valor a mostrar
  const workOrderValue = (displayClientNumber && displayProjectNumber)
    ? formatWorkOrderNumber(displayClientNumber, displayProjectNumber)
    : '----/--';

  const hasWorkOrderNumber = !!(displayClientNumber && displayProjectNumber);

  return (
    <div className="form-section">
      {/* Número de Orden de Trabajo - siempre visible */}
      <div className="form-group">
        <label htmlFor="workOrderNumber">
          {t('form.workOrderNumber')}
          {!isEditMode && hasWorkOrderNumber && <span style={{ color: 'var(--text-tertiary)', fontSize: '0.875rem', marginLeft: '0.5rem' }}>(Provisional)</span>}
        </label>
        <input
          id="workOrderNumber"
          type="text"
          value={workOrderValue}
          readOnly
          disabled
          className={`form-input work-order-readonly ${!hasWorkOrderNumber ? 'work-order-empty' : ''}`}
          title={isEditMode ? t('form.workOrderNumberHelp') : 'Este número se asignará al guardar el proyecto'}
          placeholder="Selecciona un cliente primero"
        />
        {hasWorkOrderNumber && (
          <small className="form-help">
            {isEditMode
              ? t('form.workOrderNumberHelp')
              : 'Este número se asignará definitivamente al guardar el proyecto'}
          </small>
        )}
      </div>

      {/* Nombre */}
      <div className="form-group">
        <label htmlFor="name">
          {t('form.projectName')} <span className="required">{t('form.required')}</span>
        </label>
        <input
          id="name"
          type="text"
          value={formData.name}
          onChange={(e) => onFormDataChange({ name: e.target.value })}
          required
          disabled={isLoading}
          placeholder={t('form.projectNamePlaceholder')}
          className="form-input"
        />
      </div>

      {/* Descripción */}
      <div className="form-group">
        <label htmlFor="description">{t('form.description')}</label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => onFormDataChange({ description: e.target.value })}
          disabled={isLoading}
          placeholder={t('form.descriptionPlaceholder')}
          className="form-textarea"
          rows={4}
        />
      </div>

      {/* Tenant (solo en crear) */}
      {!isEditMode && (
        <div className="form-group">
          <label htmlFor="tenantId">
            {t('form.organization')} <span className="required">{t('form.required')}</span>
          </label>
          <select
            id="tenantId"
            value={formData.tenantId}
            onChange={(e) => onFormDataChange({ tenantId: e.target.value })}
            disabled={isLoading || loadingTenants}
            required
            className={`form-select ${formData.tenantId ? 'has-value' : ''}`}
          >
            <option value="">
              {loadingTenants ? 'Cargando clientes...' : 'Seleccionar Municipio / Cliente'}
            </option>
            {tenants.map(tenant => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
          {tenants.length === 0 && !loadingTenants && (
            <small className="form-help" style={{ color: 'var(--warning)' }}>
              No hay clientes disponibles. Debe crear un cliente primero.
            </small>
          )}
        </div>
      )}

    </div>
  );
}
